import React, { useState, useRef } from 'react';

const SimpleRichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
    const editorRef = useRef(null);
    const [isPreview, setIsPreview] = useState(false);

    const insertTag = (openTag, closeTag = null) => {
        const editor = editorRef.current;
        const start = editor.selectionStart;
        const end = editor.selectionEnd;
        const selectedText = editor.value.substring(start, end);
        
        let newText;
        if (closeTag) {
            newText = editor.value.substring(0, start) + 
                     openTag + selectedText + closeTag + 
                     editor.value.substring(end);
        } else {
            newText = editor.value.substring(0, start) + 
                     openTag + 
                     editor.value.substring(end);
        }
        
        onChange(newText);
        
        // Set cursor position after the inserted tag
        setTimeout(() => {
            const newPosition = start + openTag.length + selectedText.length;
            editor.focus();
            editor.setSelectionRange(newPosition, newPosition);
        }, 0);
    };

    const toolbarButtons = [
        { label: 'H1', action: () => insertTag('<h1>', '</h1>'), icon: 'title' },
        { label: 'H2', action: () => insertTag('<h2>', '</h2>'), icon: 'title' },
        { label: 'H3', action: () => insertTag('<h3>', '</h3>'), icon: 'title' },
        { label: 'B', action: () => insertTag('<strong>', '</strong>'), icon: 'format_bold' },
        { label: 'I', action: () => insertTag('<em>', '</em>'), icon: 'format_italic' },
        { label: 'U', action: () => insertTag('<u>', '</u>'), icon: 'format_underlined' },
        { label: 'UL', action: () => insertTag('<ul>\n<li>', '</li>\n</ul>'), icon: 'format_list_bulleted' },
        { label: 'OL', action: () => insertTag('<ol>\n<li>', '</li>\n</ol>'), icon: 'format_list_numbered' },
        { label: 'LI', action: () => insertTag('<li>', '</li>'), icon: 'list' },
        { label: 'P', action: () => insertTag('<p>', '</p>'), icon: 'notes' },
        { label: 'BR', action: () => insertTag('<br>'), icon: 'keyboard_return' },
    ];

    return (
        <div className={`border border-gray-200 rounded-xl overflow-hidden ${className}`}>
            {/* Toolbar */}
            <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1">
                {toolbarButtons.map((button, index) => (
                    <button
                        key={index}
                        type="button"
                        onClick={button.action}
                        className="px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"
                        title={button.label}
                    >
                        <span className="material-icons-round text-sm">{button.icon}</span>
                        <span className="hidden sm:inline">{button.label}</span>
                    </button>
                ))}
                <div className="ml-auto flex gap-1">
                    <button
                        type="button"
                        onClick={() => setIsPreview(!isPreview)}
                        className={`px-3 py-1 text-xs border rounded transition-colors flex items-center gap-1 ${
                            isPreview 
                                ? 'bg-blue-500 text-white border-blue-500' 
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                    >
                        <span className="material-icons-round text-sm">
                            {isPreview ? 'edit' : 'preview'}
                        </span>
                        <span className="hidden sm:inline">{isPreview ? 'Edit' : 'Preview'}</span>
                    </button>
                </div>
            </div>

            {/* Editor/Preview Area */}
            <div className="min-h-[200px]">
                {isPreview ? (
                    <div className="p-4 prose prose-sm max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: value || '<p class="text-gray-400 italic">No content to preview</p>' }} />
                    </div>
                ) : (
                    <textarea
                        ref={editorRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        className="w-full h-full min-h-[200px] p-4 border-none outline-none resize-none font-mono text-sm leading-relaxed"
                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace' }}
                    />
                )}
            </div>

            {/* Help Text */}
            <div className="bg-gray-50 border-t border-gray-200 p-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                    <span className="material-icons-round text-xs">info</span>
                    Use the toolbar buttons to insert HTML tags. Select text first to wrap it with formatting tags.
                </span>
            </div>
        </div>
    );
};

export default SimpleRichTextEditor;